import React from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  Dialog,
  DialogContent,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as uiSlice from '../../../data/ui.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as parentOrganizationsSlice from '../../../data/parent-organizations.slice';
import * as reservationSlice from '../../../data/reservation.slice';
import ClientDialogHeader from './ClientWizardHeader';
import ClientDialogFooter from './ClientWizardFooter';
import { useTranslation } from 'react-i18next';
import clientUtils from '../../../services/clientUtils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import {
  ClientAddress,
  ClientContact,
} from '../../../data/models/client.model';
import ProfileForm from './ProfileForm';
import ClientAddresses from './ClientAddresses';
import AddressDialog from './AddressDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import ContactDialog from './ContactDialog';
import ClientContacts from './ClientContacts';
import ClientProfile from './ClientProfile';
import { ArrowBackIos } from '@mui/icons-material';

const PREFIX = 'ClientWizard';

const classes = {
  content: `${PREFIX}-content`,
  title: `${PREFIX}-title`,
  details: `${PREFIX}-details`,
  contentPaper: `${PREFIX}-contentPaper`,
  stepContainer: `${PREFIX}-stepContainer`,
  previousButton: `${PREFIX}-previousButton`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogActions-root': {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    paddingLeft: theme.spacing(18),
    paddingRight: theme.spacing(18),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
    marginBottom: theme.spacing(4),
  },

  [`& .${classes.details}`]: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: theme.spacing(2),
    gap: theme.spacing(3),
    flexWrap: 'wrap',
  },

  [`& .${classes.contentPaper}`]: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(4),
    borderRadius: 3,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(4),
    height: 'fit-content',
  },

  [`& .${classes.stepContainer}`]: {
    width: '100%',
  },

  [`& .${classes.previousButton}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '157%',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(0.5),
  },
}));

interface ClientWizardProps {
  title: string;
  open: boolean;
  close: () => void;
}

export default function ClientWizard({
  title,
  open,
  close,
}: ClientWizardProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const client = useAppSelector(clientSlice.selectCurrentClient);
  const addresses = useAppSelector(clientSlice.selectAddresses);
  const contacts = useAppSelector(clientSlice.selectContacts);
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const parentOrganizations = useAppSelector(
    parentOrganizationsSlice.selectParentOrganizations,
  );
  const clientVerticals = useAppSelector(lookupSlice.selectClientVerticals);

  const [editingAddress, setEditingAddress] = React.useState<ClientAddress>();
  const [editingContact, setEditingContact] = React.useState<ClientContact>();
  const [addressDialogOpen, setAddressDialogOpen] = React.useState(false);
  const [confirmDialogTitle, setConfirmDialogTitle] = React.useState('');
  const [contactDialogOpen, setContactDialogOpen] = React.useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = React.useState('');
  const [confirmDialogPostiveFn, setConfirmDialogPostiveFn] = React.useState<
    () => void
  >(() => null);
  const [confirmDialogNegativeFn, setConfirmDialogNegativeFn] = React.useState<
    () => void
  >(() => null);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [isSaved, setIsSaved] = React.useState(false);

  React.useEffect(() => {
    dispatch(parentOrganizationsSlice.fetchParentOrganizationsAsync());
  }, []);
  const savingReservation = useAppSelector(
    reservationSlice.selectSavingReservation,
  );

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: clientUtils.getDefaults(),
  });

  const steps = [
    t('client.profile'),
    t('client.addresses'),
    t('client.contacts'),
    t('client.summary'),
  ];
  const [activeStep, setActiveStep] = React.useState(0);

  React.useEffect(() => {
    if (activeStep === 0 && saveSuccessMessage) {
      setActiveStep(1);
      setIsSaved(true);
    }
  }, [saveSuccessMessage, activeStep]);

  const saveClient = (data: any) => {
    const clientData = clientUtils.getClientFromFormData(data, !isSaved);
    const { effectiveStartDate, effectiveEndDate, isInternal, ...client } =
      clientData;

    if (isSaved) {
      dispatch(
        clientSlice.updateClientAsync(
          client,
          effectiveStartDate,
          effectiveEndDate,
        ),
      );
    } else {
      dispatch(
        clientSlice.createClientAsync(
          client,
          effectiveStartDate,
          effectiveEndDate,
        ),
      );
    }
  };

  const handlePrevious = () => {
    dispatch(uiSlice.clearMessages());
    setActiveStep(activeStep - 1);
  };

  const handleNext = (data: any) => {
    if (activeStep === 0) {
      saveClient(data);
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleClose = () => {
    formHook.reset();
    setActiveStep(0);
    formHook.reset();
    if (client) {
      navigate(`/clients/${client.clientBC}`);
    }

    dispatch(reservationSlice.reset());
    close();
  };

  const handleCancel = () => {
    setActiveStep(0);
    formHook.reset();
    setIsSaved(false);
    dispatch(reservationSlice.reset());
    close();
  };

  const addAddress = () => {
    setEditingAddress(undefined);
    setAddressDialogOpen(true);
  };

  const editAddress = (address: ClientAddress) => {
    setEditingAddress(address);
    setAddressDialogOpen(true);
  };

  const activateAddress = (address: ClientAddress) => {
    const confirm = () => {
      dispatch(
        clientSlice.activateClientAddressAsync(client!.clientBC, address),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmActivateAddressTitle'));
    setConfirmDialogMessage(t('client.confirmActivateAddressMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deactivateAddress = (address: ClientAddress) => {
    const confirm = () => {
      dispatch(
        clientSlice.deactivateClientAddressAsync(client!.clientBC, address),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeactivateAddressTitle'));
    setConfirmDialogMessage(t('client.confirmDeactivateAddressMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deleteAddress = (address: ClientAddress) => {
    setEditingAddress(address);
    setConfirmDialogTitle(t('client.confirmDeleteAddressTitle'));
    setConfirmDialogMessage(t('client.confirmDeleteAddressMessage'));
    setConfirmDialogPostiveFn(() => confirmDeleteAddress);
    setConfirmDialogNegativeFn(() => cancelDeleteAddress);
    setConfirmDialogOpen(true);
  };

  const confirmDeleteAddress = () => {
    dispatch(
      clientSlice.deleteClientAddressAsync(client!.clientBC, editingAddress!),
    );
    setConfirmDialogOpen(false);
  };

  const cancelDeleteAddress = () => {
    setConfirmDialogOpen(false);
  };

  const closeAddressDialog = () => {
    setAddressDialogOpen(false);
  };

  const addContact = () => {
    setEditingContact(undefined);
    setContactDialogOpen(true);
  };

  const editContact = (contact: ClientContact) => {
    setEditingContact(contact);
    setContactDialogOpen(true);
  };

  const activateContact = (contact: ClientContact) => {
    const confirm = () => {
      dispatch(
        clientSlice.activateClientContactAsync(client!.clientBC, contact),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmActivateContactTitle'));
    setConfirmDialogMessage(t('client.confirmActivateContactMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deactivateContact = (contact: ClientContact) => {
    const confirm = () => {
      dispatch(
        clientSlice.deactivateClientContactAsync(client!.clientBC, contact),
      );
      setConfirmDialogOpen(false);
    };
    const cancel = () => {
      setConfirmDialogOpen(false);
    };
    setConfirmDialogTitle(t('client.confirmDeactivateContactTitle'));
    setConfirmDialogMessage(t('client.confirmDeactivateContactMessage'));
    setConfirmDialogPostiveFn(() => confirm);
    setConfirmDialogNegativeFn(() => cancel);
    setConfirmDialogOpen(true);
  };

  const deleteContact = (contact: ClientContact) => {
    setEditingContact(contact);
    setConfirmDialogTitle(t('client.confirmDeleteContactTitle'));
    setConfirmDialogMessage(t('client.confirmDeleteContactMessage'));
    setConfirmDialogPostiveFn(() => confirmDeleteContact);
    setConfirmDialogNegativeFn(() => cancelDeleteContact);
    setConfirmDialogOpen(true);
  };

  const confirmDeleteContact = () => {
    dispatch(
      clientSlice.deleteClientContactAsync(client!.clientBC, editingContact!),
    );
    setConfirmDialogOpen(false);
  };

  const cancelDeleteContact = () => {
    setConfirmDialogOpen(false);
  };

  const closeContactDialog = () => {
    setContactDialogOpen(false);
  };

  return (
    <RootDialog fullScreen open={open} onClose={handleClose}>
      <ClientDialogHeader
        passengerName={`${client?.legalName || ''}`}
        title={title}
        steps={steps}
        activeStep={activeStep}
        isValid={formHook.formState.isValid}
        step={handleStep}
        close={handleClose}
      />
      {savingReservation && <LinearProgress />}
      <DialogContent className={classes.content}>
        <Paper
          className={classes.contentPaper}
          sx={{ width: activeStep === 3 ? '100%' : undefined }}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            {activeStep === 0 && (
              <Box>
                <Typography variant="h5" className={classes.title}>
                  {`1. ${t('client.profile')}`}
                </Typography>
                <ProfileForm
                  isNew={!isSaved}
                  parentOrganizations={parentOrganizations}
                  clientVerticals={clientVerticals}
                  formHook={formHook}
                />
              </Box>
            )}

            {activeStep === 1 && (
              <Box className={classes.stepContainer}>
                <Box
                  className={classes.previousButton}
                  onClick={handlePrevious}>
                  <ArrowBackIos sx={{ fontSize: 14 }} />
                  {t('common.back')}
                </Box>
                <Typography variant="h5" className={classes.title}>
                  {`2. ${t('client.addresses')}`}
                </Typography>
                <ClientAddresses
                  clientAddresses={addresses}
                  addAddress={addAddress}
                  editAddress={editAddress}
                  activateAddress={activateAddress}
                  deactivateAddress={deactivateAddress}
                  deleteAddress={deleteAddress}
                  readonly={false}
                />
              </Box>
            )}

            {activeStep === 2 && (
              <Box className={classes.stepContainer}>
                <Box
                  className={classes.previousButton}
                  onClick={handlePrevious}>
                  <ArrowBackIos sx={{ fontSize: 14 }} />
                  {t('common.back')}
                </Box>
                <Typography variant="h5" className={classes.title}>
                  {`3. ${t('client.contacts')}`}
                </Typography>
                <ClientContacts
                  clientContacts={contacts}
                  addContact={addContact}
                  editContact={editContact}
                  activateContact={activateContact}
                  deactivateContact={deactivateContact}
                  deleteContact={deleteContact}
                  readonly={false}
                />
              </Box>
            )}

            {activeStep === 3 && (
              <Box className={classes.stepContainer}>
                <Box
                  className={classes.previousButton}
                  onClick={handlePrevious}>
                  <ArrowBackIos sx={{ fontSize: 14 }} />
                  {t('common.back')}
                </Box>
                <Typography variant="h5" className={classes.title}>
                  {`4. ${t('client.summary')}`}
                </Typography>
                {/* <ClientDetails client={client || undefined} readonly={true} /> */}
                <Box className={classes.details}>
                  <ClientProfile
                    client={client || undefined}
                    edit={() => null}
                    activate={() => null}
                    deactivate={() => null}
                    readonly={true}
                  />
                  <ClientAddresses
                    clientAddresses={addresses}
                    addAddress={addAddress}
                    editAddress={editAddress}
                    activateAddress={activateAddress}
                    deactivateAddress={deactivateAddress}
                    deleteAddress={deleteAddress}
                    readonly={true}
                  />
                  <ClientContacts
                    clientContacts={contacts}
                    addContact={addContact}
                    editContact={editContact}
                    activateContact={activateContact}
                    deactivateContact={deactivateContact}
                    deleteContact={deleteContact}
                    readonly={true}
                  />
                </Box>
              </Box>
            )}
          </LocalizationProvider>
          {savingReservation && <LinearProgress />}
          <ClientDialogFooter
            isFirst={activeStep === 0}
            isLast={activeStep === 3}
            isSaving={savingReservation}
            isValid={formHook.formState.isValid}
            next={formHook.handleSubmit(handleNext)}
            close={handleClose}
            cancel={handleCancel}
          />
        </Paper>
      </DialogContent>
      <AddressDialog
        title={
          editingAddress ? t('client.editAddress') : t('client.addAddress')
        }
        open={addressDialogOpen}
        client={client!}
        address={editingAddress}
        close={closeAddressDialog}
      />
      <ContactDialog
        title={
          editingContact ? t('client.editContact') : t('client.addContact')
        }
        open={contactDialogOpen}
        client={client!}
        contact={editingContact}
        close={closeContactDialog}
      />
      <ConfirmationDialog
        title={confirmDialogTitle}
        message={confirmDialogMessage}
        open={confirmDialogOpen}
        positiveAction={confirmDialogPostiveFn}
        negativeAction={confirmDialogNegativeFn}
      />
    </RootDialog>
  );
}
